import { auth } from './config';
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

// ここから各メソッド部

// ポスターIDで投稿情報取得（複数ドキュメント対応）
export const getPosterDoc = async (sessionId, posterId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/get_poster_doc`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify({ poster_id: posterId }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Get poster doc error:', error);
    throw error;
  }
};

// ポスター説明・コメント・タグ登録
export const registerPosterDoc = async (sessionId, payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/register_poster_doc`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Register poster doc error:', error);
    throw error;
  }
};

/** コメント1件に思考フェーズタグ（phase_tags）のみを付与 */
export const assignCommentPhaseTags = async (sessionId, { poster_id, doc_id }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/assign_comment_phase_tags`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionId}`,
      },
      body: JSON.stringify({ poster_id, doc_id }),
    });
    if (response.status === 402) {
      let payload = {};
      try {
        payload = await response.json();
      } catch (_) {
        /* ignore */
      }
      const detail = payload.detail || payload;
      const err = new Error(detail.message || 'Payment required');
      err.paymentRequired = true;
      err.usage = detail.usage || detail;
      throw err;
    }
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    if (error.paymentRequired) {
      throw error;
    }
    console.error('Assign comment phase tags error:', error);
    throw error;
  }
};

/**
 * ログインユーザー自身の過去のコメントから生成されたパーソナライズ問いかけ（最大5件）。
 * @returns {{ questions: Array<{question, source_post_ids, source_phase, target_phase, reason, poster_id, comment_title, posterImageUrl}>, comments_in_period: number, insufficient_data: boolean, cached: boolean }}
 */
export const getPersonalizedQuestions = async (sessionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/personalized_questions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionId}`,
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Get personalized questions error:', error);
    throw error;
  }
};

/** 思考フェーズ取得の利用状況（課金要否） */
export const getPhaseTagUsageStatus = async (sessionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/phase_tag_usage_status`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionId}`,
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Get phase tag usage status error:', error);
    throw error;
  }
};

/** 一覧画面用ページング（直近150件まで・1ページ最大30件） */
export const getPosterListPaged = async (sessionId, {
  list_filter,
  page = 1,
  page_size = 30,
  filter_mode = 'recent',
  date_from,
  date_to,
  tag_exact,
  phase_tags_any,
}) => {
  try {
    const body = { list_filter, page, page_size, filter_mode };
    if (filter_mode === 'date_range' && date_from && date_to) {
      body.date_from = date_from;
      body.date_to = date_to;
    }
    if (filter_mode === 'tag' && tag_exact) {
      body.tag_exact = tag_exact;
    }
    if (filter_mode === 'phase' && Array.isArray(phase_tags_any) && phase_tags_any.length > 0) {
      body.phase_tags_any = phase_tags_any;
    }
    const response = await fetch(`${API_BASE_URL}/get_poster_list_paged`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return {
      posters: data.posters || [],
      total_count: data.total_count ?? 0,
      page: data.page ?? page,
      page_size: data.page_size ?? page_size,
      total_pages: data.total_pages ?? 0,
    };
  } catch (error) {
    console.error('Get poster list paged error:', error);
    throw error;
  }
};

/** コメント(docs)タグの部分一致候補（一覧の list_filter に準拠） */
export const searchPosterDocTags = async (sessionId, { list_filter, q }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/search_poster_doc_tags`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify({ list_filter, q: q || '' }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.tags || [];
  } catch (error) {
    console.error('Search poster doc tags error:', error);
    throw error;
  }
};

/** タグ検索モーダル用: 直近使われたコメントタグを最大20件（list_filter に準拠） */
export const getRecentPosterDocTags = async (sessionId, { list_filter }) => {
  try {
    const response = await fetch(`${API_BASE_URL}/recent_poster_doc_tags`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify({ list_filter }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.tags || [];
  } catch (error) {
    console.error('Get recent poster doc tags error:', error);
    throw error;
  }
};

// ユーザーのポスター一覧（署名付きURL）取得
export const getUserPosters = async (sessionId, sort_rule='default') => {
  try {
    const response = await fetch(`${API_BASE_URL}/get_user_posters`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify({ sort_rule: sort_rule }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    // posters: [{ posterId, imageUrl }]
    return data.posters || [];
  } catch (error) {
    console.error('Get user posters error:', error);
    throw error;
  }
};

export const getUserPosterCount = async (sessionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/get_user_poster_count`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.count || 0;
  } catch (error) {
    console.error('Get user poster count error:', error);
    throw error;
  }
};

// 月次タイトル一覧を取得（from_year_month順でソート済み）
export const getMonthlyTitles = async (sessionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/get_monthly_titles`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
    });
    if (!response.ok) {
      if (response.status === 404) {
        return []; // データがない場合は空配列を返す
      }
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.monthly_titles || [];
  } catch (error) {
    console.error('Get monthly titles error:', error);
    // エラーが発生した場合は空配列を返す
    return [];
  }
};

/** コメント単位の月次至高（monthly_comment_title）一覧 */
export const getMonthlyCommentTitles = async (sessionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/get_monthly_comment_titles`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionId}`,
      },
    });
    if (!response.ok) {
      if (response.status === 404) return [];
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.monthly_comment_titles || [];
  } catch (error) {
    console.error('Get monthly comment titles error:', error);
    return [];
  }
};

/**
 * 期間内の自分のコメントに付いた phase_tags を集計（至高コメントサマリー上部のモザイク用）
 * @returns {{ counts: Record<string, number>, comments_in_period: number }}
 */
export const getCommentPhaseDistributionForPeriod = async (
  sessionId,
  { from_year_month, to_year_month }
) => {
  try {
    const response = await fetch(`${API_BASE_URL}/comment_phase_distribution_for_period`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${sessionId}`,
      },
      body: JSON.stringify({
        from_year_month: from_year_month ?? null,
        to_year_month,
      }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Get comment phase distribution error:', error);
    throw error;
  }
};

/**
 * 公開ポータル用: 全ユーザーの直近 months ヶ月のサマリー（認証不要）
 * @returns {{ phase_counts: Record<string, number>, topic_tags: Array<{tag: string, count: number}>, comments_in_period: number, special_questions: Array<{id: string, title: string}>, from_year_month: number, to_year_month: number }}
 */
export const getPublicCommunitySummary = async ({ months = 1 } = {}) => {
  try {
    const response = await fetch(
      `${API_BASE_URL}/public/community_summary?months=${encodeURIComponent(months)}`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Get public community summary error:', error);
    throw error;
  }
};

// ポスターの署名付きURL取得
export const getPosterSignedUrl = async (sessionId, blobPath) => {
  try {
    const response = await fetch(`${API_BASE_URL}/get_poster_url`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify({ blob_path: blobPath }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.url;
  } catch (error) {
    console.error('Get poster signed URL error:', error);
    throw error;
  }
};

// ID tokenを取得してAPIリクエストを送信
export const makeAuthenticatedRequest = async (endpoint, options = {}) => {
  try {
    const user = auth.currentUser;
    if (!user) {
      throw new Error('User not authenticated');
    }

    const idToken = await user.getIdToken();
    
    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

// Google認証でバックエンドにユーザー情報を送信
export const authenticateWithGoogle = async (idToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/google`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ idToken }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const errorMessage = errorData?.detail || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Google authentication error:', error);
    throw error;
  }
};

// メール・パスワード認証でバックエンドにユーザー情報を送信
export const authenticateWithEmail = async (idToken) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/email`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ idToken }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const errorMessage = errorData?.detail || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Email authentication error:', error);
    throw error;
  }
};

// 新規登録でバックエンドにユーザー情報を送信
export const authenticateSignup = async (idToken, userInfo) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/signup`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ idToken, userInfo }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      const errorMessage = errorData?.detail || `HTTP error! status: ${response.status}`;
      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('Signup authentication error:', error);
    throw error;
  }
};

// セッション検証
export const verifySession = async (sessionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/verify-session`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${sessionId}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Session verification error:', error);
    throw error;
  }
};

// ログアウト
export const logoutFromBackend = async (sessionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/auth/logout`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${sessionId}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

// ポスタープレビュー生成
export const generatePosterPreview = async (sessionId, payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/poster/generate-preview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Poster preview generation error:', error);
    throw error;
  }
};

// ポスター作成
export const createPoster = async (sessionId, payload) => {
  try {
    const response = await fetch(`${API_BASE_URL}/poster/create`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Poster creation error:', error);
    throw error;
  }
};

// コメントドキュメント削除
export const deletePosterDoc = async (sessionId, posterId, docId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/delete_poster_doc`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify({ poster_id: posterId, doc_id: docId }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('Delete poster doc error:', error);
    throw error;
  }
};

// Firestoreからquestionsコレクション取得（ファイル最下部に移動）
export const getQuestions = async () => {
  try {
    const { getFirestore, collection, getDocs } = await import('firebase/firestore');
    const db = getFirestore();
    const querySnapshot = await getDocs(collection(db, 'questions'));
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      title: doc.data().title || '',
      icon: '/img/cat.png'
    }));
  } catch (error) {
    console.error('Firestore questions取得エラー:', error);
    return [];
  }
};

export const getFavoritePosters = async (sessionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/get_favorite_posters`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      // body不要
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.posters || [];
  } catch (error) {
    console.error('Get favorite posters error:', error);
    throw error;
  }
};

// つながりポスター取得API
export async function getConnectionPosters(sessionId) {
  try {
    const response = await fetch(`${API_BASE_URL}/get_connection_posters`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    // data: [{ question, posters: [...] }]
    return data.connectionGroups || [];
  } catch (err) {
    console.error('getConnectionPosters失敗:', err);
    return [];
  }
}

// お気に入り削除API
export async function deleteFavoritePoster(sessionId, posterId) {
  try {
    const response = await fetch(`${API_BASE_URL}/delete_favorite_poster`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify({ poster_id: posterId }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.success;
  } catch (err) {
    console.error('deleteFavoritePoster失敗:', err);
    return false;
  }
}

// お気に入り追加API
export async function addFavoritePoster(sessionId, posterId) {
  try {
    const response = await fetch(`${API_BASE_URL}/add_favorite_poster`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify({ poster_id: posterId }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.success;
  } catch (err) {
    console.error('addFavoritePoster失敗:', err);
    return false;
  }
}

// ポスター削除API（論理削除）
export async function deletePoster(sessionId, posterId) {
  try {
    const response = await fetch(`${API_BASE_URL}/delete_poster`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify({ poster_id: posterId }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    const data = await response.json();
    return data.success;
  } catch (err) {
    console.error('deletePoster失敗:', err);
    return false;
  }
}

// posterIdからポスター詳細情報取得
/** コメント関連提案一覧（ページング） */
export const suggestRelatedDocs = async (sessionId, {
  poster_id,
  source_doc_id,
  tab = 'own',
  page = 1,
  page_size = 4,
  vector_only,
}) => {
  const body = {
    poster_id,
    source_doc_id,
    tab,
    page,
    page_size,
  };
  if (vector_only === true) {
    body.vector_only = true;
  }
  const response = await fetch(`${API_BASE_URL}/suggest_related_docs`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionId}`,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    const errText = await response.text();
    throw new Error(errText || `HTTP error ${response.status}`);
  }
  return response.json();
};

/** docs 間の関連 / お気に入り紐付け（doc_associations） */
export const createDocAssociation = async (sessionId, {
  poster_id,
  source_doc_id,
  target_doc_id,
  kind,
}) => {
  const response = await fetch(`${API_BASE_URL}/create_doc_association`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionId}`,
    },
    body: JSON.stringify({
      poster_id,
      source_doc_id,
      target_doc_id,
      kind,
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const d = data.detail;
    let msg = `HTTP error ${response.status}`;
    if (typeof d === 'string') msg = d;
    else if (Array.isArray(d) && d[0]?.msg) msg = d.map((x) => x.msg).join(', ');
    else if (data.message) msg = data.message;
    throw new Error(msg);
  }
  return data;
};

/** docs 間の関連解除（doc_associations） */
export const deleteDocAssociation = async (sessionId, {
  association_id,
}) => {
  const response = await fetch(`${API_BASE_URL}/delete_doc_association`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionId}`,
    },
    body: JSON.stringify({
      association_id,
    }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const d = data.detail;
    let msg = `HTTP error ${response.status}`;
    if (typeof d === 'string') msg = d;
    else if (Array.isArray(d) && d[0]?.msg) msg = d.map((x) => x.msg).join(', ');
    else if (data.message) msg = data.message;
    throw new Error(msg);
  }
  return data;
};

/** 自分のポスターに紐づくコメント関連（doc_associations）一覧 */
export const listDocAssociations = async (sessionId, { poster_id }) => {
  const response = await fetch(`${API_BASE_URL}/list_doc_associations`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${sessionId}`,
    },
    body: JSON.stringify({ poster_id }),
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const d = data.detail;
    let msg = `HTTP error ${response.status}`;
    if (typeof d === 'string') msg = d;
    else if (Array.isArray(d) && d[0]?.msg) msg = d.map((x) => x.msg).join(', ');
    else if (data.message) msg = data.message;
    throw new Error(msg);
  }
  return data;
};

export const getPosterInfo = async (sessionId, posterId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/get_poster`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify({ poster_id: posterId }),
    });
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.json();
  } catch (error) {
    console.error('getPosterInfo error:', error);
    throw error;
  }
}

/**
 * Fetch suggested posters based on the given poster ID.
 * @param {string} posterId - The ID of the current poster.
 * @returns {Promise<Array>} - A promise that resolves to an array of suggested posters.
 */
export const getSuggestPosters = async (sessionId, posterId) => {
  const response = await fetch(`${API_BASE_URL}/get-suggest-poster?posterId=${posterId}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${sessionId}`,
    },
  });
  if (!response.ok) {
    throw new Error('Failed to fetch suggested posters');
  }
  return response.json();
};

/**
 * Create Stripe Checkout Session
 * @param {string} sessionId - User session ID
 * @param {Object} data - Checkout session data
 * @returns {Promise<Object>} - Checkout session data with sessionId
 */
export const createCheckoutSession = async (sessionId, data) => {
  try {
    const response = await fetch(`${API_BASE_URL}/create_checkout_session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify(data),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Create checkout session error:', error);
    throw error;
  }
};

/**
 * Update user premium status after successful payment
 * @param {string} sessionId - User session ID
 * @param {string} stripeSessionId - Stripe checkout session ID
 * @returns {Promise<Object>} - Premium status update result
 */
export const updatePremiumStatus = async (sessionId, stripeSessionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/update_premium_status`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify({ sessionId: stripeSessionId }),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Update premium status error:', error);
    throw error;
  }
};

/**
 * Check if user has paid for the premium 4th comment feature
 * @param {string} sessionId - User session ID
 * @returns {Promise<Object>} - Payment status result with is_paid boolean and message
 */
export const checkPaymentStatus = async (sessionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/is_pay_comment53`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Check payment status error:', error);
    throw error;
  }
};

/**
 * コンテンツモデレーション - テキストが適切かチェック
 */
export const moderateContent = async (sessionId, textLines) => {
  try {
    const response = await fetch(`${API_BASE_URL}/moderate_content`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify({ text_lines: textLines }),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Content moderation error:', error);
    // エラー時はデフォルトで許可
    return { allowed: true, message: 'モデレーションエラーのため、コンテンツを許可します', flagged_keywords: [] };
  }
};

/**
 * 退会処理 - ユーザーの退会フラグを設定
 */
export const withdrawUser = async (sessionId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/withdraw`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Withdraw user error:', error);
    throw error;
  }
};

// ポスターの共有設定を更新
export const updatePosterShareSetting = async (sessionId, posterId, shareStatus = 'ok') => {
  try {
    const response = await fetch(`${API_BASE_URL}/poster/share`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${sessionId}`,
      },
      body: JSON.stringify({ 
        poster_id: posterId,
        share: shareStatus 
      }),
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Update poster share setting error:', error);
    throw error;
  }
};
